import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, defer, from, of, throwError } from 'rxjs';
import { switchMap, catchError, mergeMap, timeout } from 'rxjs/operators';
import { KeycloakService } from '@inoventoryapps/keycloak-angular';
import { ActivatedRoute } from '@angular/router';
// import { API_URL } from '../../_constants/urls.constant';
import { KeyService } from './services/key.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class Encryption2Interceptor implements HttpInterceptor {

  excludedRoutes = [];
  forceEncryptionRoutes = ['lokos/permission'];

  constructor(
    private keyService: KeyService,
    private keyclockService: KeycloakService,
    private activatedRoute: ActivatedRoute
  ) { }

  /**
   * For encryption and decryption of data
   * @param req
   * @param next
   * @returns encrypted data
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Get debug parameter from route
    // // If encryption is bypassed or disabled, proceed normally
    // if ((!this.isByPass(req) || this.isDebug || !environment.enableEncryption) && !this.isForceEncryption(req)) {
    //   console.log('Encryption bypassed for:', req.url);
    //   return next.handle(req);
    // }

    console.log('Processing encrypted request for:', req.url);

    // Handle encryption/decryption with timeout and error handling
    return defer(() => this.processRequest(req, next)).pipe(
    mergeMap(event$ => event$), // unwrap Observable<HttpEvent>
    timeout({
      each: 30000,
      with: () =>
        throwError(() =>
          new HttpErrorResponse({
            status: 408,
            statusText: 'Encryption timeout',
            error: 'Request timed out during encryption'
          })
        )
    }),
    catchError(error => {
      console.error('Encryption interceptor error:', error);
      return throwError(() => error);
    })
  );
  }

private async processRequest(
  req: HttpRequest<any>,
  next: HttpHandler
): Promise<Observable<HttpEvent<any>>> {

  // 1️⃣ Client key is a prerequisite → MUST block on failure
  try {
    await Promise.race([
      this.keyService.getClientKey(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Client key timeout')), 10000)
      )
    ]);
  } catch (err) {
    console.error('Client key fetch failed', err);
    throw err; // block API
  }

  // 2️⃣ Determine base URL
  const url = environment.production ? environment.keyCloackApiUrlV2 : (req.url.includes(environment.KEYCLOCK) ? environment.adminLokosBaseUrl : environment.baseUrl);

  // 3️⃣ JWKS / server public key → MUST block on failure
  let serverPublicKey: any;
  try {
    serverPublicKey = await Promise.race([
      this.keyService.getServerPublicKey(`${url}api/crypto/jwks`, req.url),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Server key timeout')), 10000)
      )
    ]);
  } catch (err) {
    console.error('JWKS fetch failed — blocking request', err);
    throw err; // block API
  }

  // 4️⃣ Encrypt body (optional → do NOT block)
  let encryptedBody: any = req.body;

  if (this.isByPass(req)) {
    try {
      encryptedBody = await this.keyclockService.encrypt(
        { publicKey: serverPublicKey },
        req.body
      );
    } catch (encryptError) {
      console.error('Encryption failed — continuing without encryption', encryptError);
      encryptedBody = req.body;
    }
  }

  // 5️⃣ Prepare headers
  let setHeaders: any = {
    'x-encrypted': this.isByPass(req) ? 'true' : 'false'
  };

  // 6️⃣ DPoP (optional → do NOT block)
  if (!req.url.includes('signer')) {
    try {
      setHeaders = {
        ...setHeaders,
        DPoP: await this.keyclockService.dpopProof(
          req.url,
          req.method,
          encryptedBody?.iv || '',
          this.keyService.getKid(req.url)
        )
      };
    } catch (dpopError) {
      console.error('DPoP generation failed — continuing without DPoP', dpopError);
    }
  }

  // 7️⃣ Clone request
  const newReq = req.clone({
    body:
      this.isByPass(req)
        ? { data: encryptedBody.encrypted }
        : req.body,
    setHeaders
  });

  // 8️⃣ Handle request + decrypt response
  return next.handle(newReq).pipe(
    switchMap((event: HttpEvent<any>) => {
      if (event instanceof HttpResponse && event.headers.get('x-encrypted')) {
        return from(this.decryptResponse(event, serverPublicKey));
      }
      return of(event);
    }),
    catchError((error: HttpErrorResponse) => {
      if (error instanceof HttpErrorResponse && newReq.headers.get('x-encrypted')) {
        return from(this.decryptError(error, serverPublicKey)).pipe(
          switchMap(decryptedError => {
            throw decryptedError;
          })
        );
      }
      throw error;
    })
  );
}


  private async decryptResponse(event: HttpResponse<any>, serverPublicKey: any): Promise<HttpResponse<any>> {
    try {
      if (event.body && event.body['data']) {
        const decodedBody = await this.keyclockService.decrypt(
          {publicKey: serverPublicKey},
          event.body['data'],
          event.headers.get('x-crypto-enabled')
        );
        return event.clone({ body: decodedBody });
      } else {
        return event.clone({ body: {} });
      }
    } catch (error) {
      console.error('Decryption error:', error);
      return event;
    }
  }

  private async decryptError(error: HttpErrorResponse, serverPublicKey: any): Promise<HttpErrorResponse> {
    try {
      if (error.error && error.error.data) {
        const decodedBody = await this.keyclockService.decrypt(
          {publicKey: serverPublicKey},
          error.error['data'],
          error.headers.get('x-crypto-enabled')
        );
        error.error.data = decodedBody;
      }
    } catch (decryptError) {
      console.error('Error decryption failed:', decryptError);
    }
    return error;
  }

  /**
   *
   * @param req
   * @returns false if encryption is disabled or the request URL contains any excluded routes
   * @returns true if encryption is enabled and the request URL does not contain any excluded routes
   */
  isByPass(req: HttpRequest<any>): boolean {
    // Check if encryption is enabled globally
    if (!environment.enableEncryption) {
      return false; // Bypass encryption when globally disabled
    }

    // Check if the request URL contains any excluded routes
    const isExcludedRoute = this.excludedRoutes.some((route: any) => req.url.includes(route));

    if (isExcludedRoute) {
      console.log('Encryption bypassed for excluded route:', req.url);
      return false; // Bypass encryption for excluded routes
    }

    return true; // Enable encryption for this request
  }

// isByPass(req: HttpRequest<any>): boolean {
//     return environment.enableEncryption && !(this.excludedRoutes.some((route: any) => req.url.includes(route)));
//   }

  isForceEncryption(req: HttpRequest<any>): boolean {
    return this.forceEncryptionRoutes.some((route: any) => req.url.includes(route));
  }
}
